import { useEffect, useState } from 'react';

import { ArrowBack, CloseRounded, InfoOutlined } from '@mui/icons-material';
import { Alert, Box, IconButton, SxProps, Theme, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import { DynamicSignatureFormQuestion } from '../../types/taskForm';
import { DynamicSignatureQuestion } from './DynamicSignatureQuestion';
import { EmptyDynamicSignatureQuestionsState } from './EmptyDynamicSignatureQuestionsState';

const customStyles: Record<string, SxProps<Theme>> = {
  drawerBox: {
    flexDirection: 'column',
    display: 'flex',
    height: '100%',
  },
  contentBox: {
    padding: '16px',
    flex: 1,
    overflowY: 'auto',
    width: '440px',
  },
  headerBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    marginBottom: '8px',
    paddingBottom: '0px',
    flexShrink: 0,
  },
  backButton: {
    display: 'flex',
    gap: '8px',
    color: (theme) => theme.palette.primary.main,
    cursor: 'pointer',
  },
  backButtonText: {
    fontSize: '14px',
    lineHeight: '24px',
    fontWeight: 400,
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '32px',
    textAlign: 'left',
    marginBottom: '16px',
  },
  closeButton: {
    color: '#666',
  },
  infoBox: {
    backgroundColor: '#D8EFFE',
    borderWidth: '1px 1px 1px 5px',
    borderStyle: 'solid',
    borderColor: '#8BC6FD',
    borderRadius: '12px',
    padding: '0px 0px 0px 0px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    marginLeft: '8px',
    marginRight: '8px',
  },
  infoIcon: {
    color: '#1976D2',
    fontSize: '20px',
    marginTop: '1.6px',
    marginLeft: '4px',
  },
  infoText: {
    color: (theme) => theme.palette.text.primary,
    fontSize: '14px',
    lineHeight: '22px',
    marginRight: '24px',
  },
  infoTextBold: {
    fontWeight: 500,
  },
  sectionTitle: {
    fontWeight: 500,
    marginBottom: '16px',
    marginLeft: '8px',
    color: (theme) => theme.palette.text.primary,
  },
  questionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '16px',
    marginLeft: '8px',
    marginRight: '8px',
  },
};

interface DynamicSignerDrawerContentSelectorProps {
  open?: boolean;
  onClose: () => void;
  onDynamicSignersSelected: (
    dynamicSignatureQuestionsSelected: DynamicSignatureFormQuestion[],
  ) => void;
  onBack?: () => void;
  dynamicSignatureQuestionsAvailable: DynamicSignatureFormQuestion[];
  dynamicSignatureQuestionsSelected?: DynamicSignatureFormQuestion[];
  onSelectedQuestionIdsChange: (selectedQuestionIds: string[]) => void;
}

export function DynamicSignerDrawerContentSelector({
  open = false,
  onClose,
  onDynamicSignersSelected,
  onBack,
  dynamicSignatureQuestionsAvailable,
  dynamicSignatureQuestionsSelected = [],
  onSelectedQuestionIdsChange,
}: DynamicSignerDrawerContentSelectorProps) {
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const lang = useLanguage();

  useEffect(() => {
    if (open) {
      const ids = dynamicSignatureQuestionsSelected.map((q) => String(q.id));
      setSelectedQuestionIds(ids);
    }
  }, [open, dynamicSignatureQuestionsSelected]);

  const handleQuestionToggle = (questionId: number | string) => {
    const stringId = String(questionId);
    setSelectedQuestionIds((prev) => {
      const isCurrentlySelected = prev.includes(stringId);
      return isCurrentlySelected ? prev.filter((id) => id !== stringId) : [...prev, stringId];
    });
  };

  const getSelectedQuestions = () => {
    return dynamicSignatureQuestionsAvailable
      .filter((q) => selectedQuestionIds.includes(String(q.id)))
      .map((q) => ({ id: q.id, title: q.title }));
  };

  const handleCancel = () => {
    onClose();
  };

  const handleBack = () => {
    const dynamicSignatureQuestionsSelected = getSelectedQuestions();
    onDynamicSignersSelected(dynamicSignatureQuestionsSelected);
    onBack?.();
  };

  const isQuestionSelected = (questionId: number | string) => {
    return selectedQuestionIds.includes(String(questionId));
  };

  useEffect(() => {
    onSelectedQuestionIdsChange(selectedQuestionIds);
  }, [selectedQuestionIds, onSelectedQuestionIdsChange]);

  return (
    <Box sx={customStyles.drawerBox}>
      <Box sx={customStyles.headerBox}>
        <Box
          sx={customStyles.backButton}
          onClick={handleBack}
        >
          <ArrowBack />
          <Typography sx={customStyles.backButtonText}>
            {lang.formSignatureBuilder.changeSelection}
          </Typography>
        </Box>
        <IconButton
          onClick={handleCancel}
          sx={customStyles.closeButton}
        >
          <CloseRounded />
        </IconButton>
      </Box>
      <Box sx={customStyles.contentBox}>
        <Box sx={customStyles.titleContainer}>
          <Typography sx={customStyles.title}>{lang.formSignatureBuilder.dynamicSigner}</Typography>
        </Box>

        <Alert
          severity='info'
          sx={customStyles.infoBox}
          icon={<InfoOutlined sx={customStyles.infoIcon} />}
        >
          <Typography sx={customStyles.infoText}>
            <Typography component='span'>
              {lang.formSignatureBuilder.dynamicSignerDescription}
            </Typography>{' '}
            <Typography
              component='span'
              sx={customStyles.infoTextBold}
            >
              {lang.formSignatureBuilder.dynamicSignerDescriptionBold}
            </Typography>
          </Typography>
        </Alert>

        <Typography sx={customStyles.sectionTitle}>
          {lang.formSignatureBuilder.formQuestionsTitle}
        </Typography>

        {dynamicSignatureQuestionsAvailable.length === 0 ? (
          <EmptyDynamicSignatureQuestionsState />
        ) : (
          <Box sx={customStyles.questionContainer}>
            {dynamicSignatureQuestionsAvailable.map((question) => (
              <DynamicSignatureQuestion
                key={question.id}
                question={question}
                isSelected={isQuestionSelected(question.id)}
                onToggle={handleQuestionToggle}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default DynamicSignerDrawerContentSelector;
